import { Icon } from "@core/components/Icon";
import { ImageBehaviour } from "@core/components/ImageBehaviour";
import type { SafeHtmlAttributes } from "@core/types/SafeHtmlAttributes";
import classnames from "classnames";

import type { DirectionalIconComponent } from "@jti/brand-icons";

import styles from "./Badge.module.scss";

export const BADGE_VARIANTS = [
  "neutral-subtle",
  "neutral-bold",
  "raised",
  "sale",
  "club-tier3",
  "club-tier2",
  "club-tier1",
  "ghost",
] as const;

export type BadgeVariant = (typeof BADGE_VARIANTS)[number];

export type BadgeCustomIcon = {
  src: string;
};

export type BadgeProps = {
  variant?: BadgeVariant;
  label?: string;
  isLabelSubtle?: boolean;
  icon?: DirectionalIconComponent;
  /**
   * @deprecated Use `icon` prop with a DirectionalIconComponent instead.
   * This is a temporary workaround for CMS-driven image icons to avoid regressions.
   */
  customIcon?: BadgeCustomIcon;
} & SafeHtmlAttributes<HTMLSpanElement>;

export const Badge = ({
  variant = "neutral-bold",
  label,
  isLabelSubtle = false,
  icon,
  customIcon,
  ...rest
}: BadgeProps) => (
  <span className={classnames(styles.badge, styles[variant], isLabelSubtle && styles.subtle)} {...rest}>
    {icon && <Icon icon={icon} size="sm" aria-hidden="true" />}
    {customIcon && (
      <ImageBehaviour
        src={customIcon.src}
        alt=""
        className={styles.customIcon}
        width={20}
        height={20}
        aria-hidden="true"
      />
    )}
    {label && (
      <span data-testid="label" className={styles.label}>
        {label}
      </span>
    )}
  </span>
);
